import * as React from "react";
import { captureException } from "~/config/sentry";
import {
  OFFLINE_UPLOAD_CONFIG,
  useOfflineUploadsStore,
} from "./offlineUploads.store";

export function useOfflineUploads() {
  const store = useOfflineUploadsStore();

  return React.useMemo(
    () => ({
      addUpload: store.addUpload,
      enforceStorageLimits: store.enforceStorageLimits,
      getById: store.getById,
      getStorageStats: store.getStorageStats,
      getStoredFile: store.getStoredFile,
      getUploadsForMessage: store.getUploadsForMessage,
      getUploadsForTask: store.getUploadsForTask,
      isProcessing: store.isProcessing,
      markComplete: store.markComplete,
      markError: store.markError,
      purgeCompleted: store.purgeCompleted,
      purgeErrors: store.purgeErrors,
      purgeOld: store.purgeOld,
      removeUpload: store.removeUpload,
      removeUploadsForMessage: store.removeUploadsForMessage,
      removeUploadsForTask: store.removeUploadsForTask,
      setProcessing: store.setProcessing,
      updateProgress: store.updateProgress,
      uploads: store.uploads,
    }),
    [store]
  );
}

export function useOfflineUploadsCleanup() {
  const { getStorageStats, purgeCompleted, purgeErrors, purgeOld } =
    useOfflineUploads();

  const performCleanup = React.useCallback(async () => {
    try {
      const stats = getStorageStats();

      // Only clean up if storage usage is high (>80% of limit)
      const storageThreshold = OFFLINE_UPLOAD_CONFIG.MAX_TOTAL_STORAGE * 0.8;

      if (stats.totalSize > storageThreshold) {
        await purgeCompleted();
        await purgeErrors();
        await purgeOld(OFFLINE_UPLOAD_CONFIG.AUTO_PURGE_HOURS);
      }

      // Don't automatically enforce storage limits - let the user control this
      // Only clean up orphaned files when storage is getting full
    } catch (error) {
      captureException(error as Error, {
        extra: { context: "offline-uploads-cleanup" },
        tags: { component: "useOfflineUploadsCleanup" },
      });
    }
  }, [getStorageStats, purgeCompleted, purgeErrors, purgeOld]);

  // Run cleanup only when the app starts
  React.useEffect(() => {
    void performCleanup();
  }, []); // Empty dependency array - only run once on mount

  // Run cleanup when user returns to the app (visibility change)
  React.useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        void performCleanup();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []); // Empty dependency array - no need to recreate event listener

  return { performCleanup };
}

export function useOfflineUploadsDebug() {
  const { getStorageStats, uploads } = useOfflineUploads();
  const [stats, setStats] = React.useState(() => getStorageStats());

  React.useEffect(() => {
    const updateStats = () => setStats(getStorageStats());

    const interval = setInterval(updateStats, 5000);

    return () => clearInterval(interval);
  }, [getStorageStats]);

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  return {
    config: OFFLINE_UPLOAD_CONFIG,
    formatBytes,
    stats,
    storageUsage: {
      max: formatBytes(OFFLINE_UPLOAD_CONFIG.MAX_TOTAL_STORAGE),
      percentage: Math.round(
        (stats.totalSize / OFFLINE_UPLOAD_CONFIG.MAX_TOTAL_STORAGE) * 100
      ),
      total: formatBytes(stats.totalSize),
    },
    uploads,
  };
}

export function useOfflineUploadsByContext(
  context: "chat" | "task",
  ownerId?: string
) {
  const { uploads } = useOfflineUploads();

  const filteredUploads = React.useMemo(() => {
    return uploads.filter((upload) => {
      if (upload.context !== context) return false;
      if (ownerId && upload.ownerId !== ownerId) return false;
      return true;
    });
  }, [uploads, context, ownerId]);

  const stats = React.useMemo(() => {
    const totalSize = filteredUploads.reduce((sum, u) => sum + u.fileSize, 0);
    const byStatus = {
      complete: filteredUploads.filter((u) => u.status === "complete"),
      error: filteredUploads.filter((u) => u.status === "error"),
      pending: filteredUploads.filter((u) => u.status === "pending"),
      uploading: filteredUploads.filter((u) => u.status === "uploading"),
    };

    return {
      byStatus,
      totalFiles: filteredUploads.length,
      totalSize,
    };
  }, [filteredUploads]);

  return {
    stats,
    uploads: filteredUploads,
  };
}
